import { Checkbox, ConfigProvider, Table } from 'antd';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { ColumnsType } from 'antd/es/table';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { SelectedIndexes, TableData } from '../types';
import { BROOKS_COREY_EXPONENT_LABEL } from '../../../../../utils/brooksCorey';

type LambdaTableProps = {
  tableData: TableData[];
  selectedIndexes: { [key: number]: boolean };
  calculateLambda: (newSelectedIndexes: SelectedIndexes) => Promise<void>
};

export function LambdaTable({
  selectedIndexes,
  tableData,
  calculateLambda
}: LambdaTableProps) {
  const { isDay } = useTheme();
  const colors = getColors(isDay);

  const handleToggleDisplay = (index: number, event: CheckboxChangeEvent) => {
    calculateLambda({ [index]: event.target.checked });
  };

  // Round long raw measurements (e.g. Sw = 0.7383073286052009) to a compact,
  // readable precision so cells don't blow the column widths out.
  const formatValue = (value: any) => {
    const num = typeof value === 'string' ? Number(value) : value;
    if (typeof num === 'number' && Number.isFinite(num)) {
      return Number(num.toFixed(4));
    }
    return value;
  };

  const renderFn = (value: any, record: TableData, index: number) => {
    const display = selectedIndexes[index] ?? false;
    const shown = formatValue(value);

    if (!display) {
      return (
        <span style={{ color: 'gray', textDecoration: 'line-through' }}>
          {shown}
        </span>
      );
    }
    return <span>{shown}</span>;
  };

  // Some columns are commented for now until we confirm which columns should be shown.
  const tableColumns: ColumnsType<TableData> = [
    { title: 'Sw', dataIndex: 'sw', key: 'SwLambda', render: renderFn },
    { title: 'Pc', dataIndex: 'pc', key: 'PcLambda', render: renderFn },
    { title: 'Corrected Sw', dataIndex: 'correctedsw', key: 'correctedsw', render: renderFn },
    { title: 'Corrected Pc', dataIndex: 'pcres', key: 'pcres', render: renderFn },
    // { title: 'Ln (Swn)', dataIndex: 'lnSwn', key: 'lnSwn', render: renderFn },
    // { title: 'Ln (Pc)', dataIndex: 'lnPc', key: 'lnPc', render: renderFn },
    // {
    //   title: '(Ln (Swn))^2',
    //   dataIndex: 'lnSwnSquared',
    //   key: 'lnSwnSquared',
    //   render: renderFn
    // },
    // {
    //   title: '(Ln (Pc))^2',
    //   dataIndex: 'lnPcSquared',
    //   key: 'lnPcSquared',
    //   render: renderFn
    // },
    // {
    //   title: 'Ln (Swn) X Ln (Pc)',
    //   dataIndex: 'lnSwnXlnPc',
    //   key: 'lnSwnXlnPc',
    //   render: renderFn
    // },
    {
      title: 'Local exponent m',
      dataIndex: 'lambdaCurve',
      key: 'lambdaCurve',
      render: renderFn
    },
    {
      title: 'Use',
      dataIndex: 'actions',
      key: 'actions',
      width: 56,
      align: 'center',
      render: (_, record, index) => {
        const display = selectedIndexes[index] ?? false;
        const onChangeFunction = handleToggleDisplay.bind(null, index);

        return (
          <Checkbox
            checked={display}
            onChange={onChangeFunction}
            disabled={!record.selectable}
          />
        );
      }
    }
  ];

  return (
    <div title={`${BROOKS_COREY_EXPONENT_LABEL}; local slopes are negative for drainage.`}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: colors.colorPrimary2,
            colorText: colors.text,

            colorBgContainer: 'transparent',
            colorBorder: colors.colorPrimary2
          },
          components: {
            Checkbox: {
              colorTextDisabled: 'gray',
              colorText: colors.text
            },
            Table: {
              headerBg: isDay ? '#fafafa' : '#1f1f1f',
              borderColor: isDay ? '#f0f0f0' : '#434343',
              rowHoverBg: isDay ? '#f5f5f5' : '#1f1f1f',
              stickyScrollBarBg: isDay ? 'rgba(0, 0, 0, 0.25)' : '#434343',
              colorBgContainer: colors.bg,
              colorBgBase: colors.bg
            }
          }
        }}
      >
        <Table
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          bordered={true}
          size="small"
          // Let the table fit its column so the "Use" checkbox is always
          // visible; long raw values are rounded above so nothing overflows.
          style={{ width: '100%' }}
        />
      </ConfigProvider>
    </div>
  );
}
